import { stageLabel } from "@staffsmith/api-types/labels";
import { type ReactNode, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { ApiError } from "./api.js";
import { Icon } from "./icons.js";

// The few building blocks the pages share. Plain HTML elements, styled in
// styles.css; no component library yet (docs/engineering-conventions.md).

// The search pill in every page header (design v0.9, V4): "/" focuses it, and
// it searches candidates by skills and meaning.
function HeaderSearch() {
  const navigate = useNavigate();
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (e.key !== "/" || e.ctrlKey || e.metaKey || e.altKey) return;
      if (t && (t.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName))) return;
      if (document.querySelector("dialog[open]")) return;
      e.preventDefault();
      input.current?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  return (
    <form
      role="search"
      className="header-search"
      onSubmit={(e) => {
        e.preventDefault();
        const q = input.current?.value.trim();
        if (q) void navigate(`/search?q=${encodeURIComponent(q)}`);
      }}
    >
      <input ref={input} type="search" aria-label="Search candidates" placeholder="Search  /" />
    </form>
  );
}

export function Page({
  title,
  context,
  actions,
  record,
  children,
}: {
  title: string;
  // A line above the title: who or what the page is about.
  context?: ReactNode;
  actions?: ReactNode;
  // A record's page (design v0.9, V5): the header card with the brand band,
  // an avatar with initials, the headline and chips.
  record?: { initials: string; headline?: ReactNode; chips?: ReactNode };
  children: ReactNode;
}) {
  if (record)
    return (
      <section className="page">
        <header className="card record-header">
          <div className="record-body">
            <span className="avatar-lg" aria-hidden="true">
              {record.initials}
            </span>
            <div>
              {context ? <p className="page-context">{context}</p> : null}
              <h1 className="record-name">{title}</h1>
              {record.headline ? <p className="muted">{record.headline}</p> : null}
            </div>
            <div className="page-actions">
              {actions}
              <HeaderSearch />
            </div>
            {record.chips ? <div className="record-chips">{record.chips}</div> : null}
          </div>
        </header>
        {children}
      </section>
    );
  return (
    <section className="page">
      <header className="page-header">
        <div>
          {context ? <p className="page-context">{context}</p> : null}
          <h1>{title}</h1>
        </div>
        <div className="page-actions">
          {actions}
          <HeaderSearch />
        </div>
      </header>
      {children}
    </section>
  );
}

export function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null;
  if (error instanceof ApiError) {
    return (
      <div className="error" role="alert">
        <strong>{error.title}</strong>
        {error.detail ? <span> {error.detail}</span> : null}
        {error.errors.length ? (
          <ul>
            {error.errors.map((e) => (
              <li key={`${e.field}:${e.message}`}>
                {e.field}: {e.message}
              </li>
            ))}
          </ul>
        ) : null}
        {error.requestId ? <small> Request {error.requestId}</small> : null}
      </div>
    );
  }
  return (
    <div className="error" role="alert">
      {error instanceof Error ? error.message : String(error)}
    </div>
  );
}

export const Loading = () => <p className="muted">Loading…</p>;

// Nothing to show. Inline by default; with `tile`, the page's empty state
// (design v0.9, V5): centered, a type tile, one sentence, and one action.
export function Empty({
  children,
  tile,
  action,
}: {
  children: ReactNode;
  tile?: string;
  action?: ReactNode;
}) {
  if (!tile) return <p className="muted">{children}</p>;
  return (
    <div className="empty">
      <span className="type-tile" aria-hidden="true">
        <Icon name={tile} size={22} />
      </span>
      <p>{children}</p>
      {action}
    </div>
  );
}

export const label = (value: string) => value.replaceAll("_", " ");

// Up to two initials, for an avatar.
export const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join("");

// Stage progress (design v0.9, V5): one segment per stage, done in the brand,
// the current one in the accent, the current stage named below. An ended
// submission shows its outcome instead.
const STAGES = [
  "sourced",
  "screened",
  "shortlist",
  "client_review",
  "interview",
  "offer",
  "placed",
];
export function StageProgress({ stage }: { stage: string }) {
  const at = STAGES.indexOf(stage);
  if (at < 0) return <span className="badge bad">{label(stage)}</span>;
  return (
    <div className="stage-progress">
      <ol>
        {STAGES.map((s, i) => (
          <li key={s} className={i < at ? "done" : i === at ? "current" : undefined}>
            <span className="visually-hidden">
              {label(s)}: {i < at ? "done" : i === at ? "current" : "ahead"}
            </span>
          </li>
        ))}
      </ol>
      <span className="now">{label(stage)}</span>
    </div>
  );
}

// The match meter (design v0.9, V5): one segment per requirement, filled for
// each one met, with its label above.
export function MatchMeter({ met, of, band }: { met: number; of: number; band: string }) {
  return (
    <span className="match-meter" title={`${met} of ${of} requirements met`}>
      <span className="label">
        {band} match · {met}/{of}
      </span>
      <span className="segments" aria-hidden="true">
        {Array.from({ length: of }, (_, i) => (
          <span key={i} className={i < met ? "on" : undefined} />
        ))}
      </span>
    </span>
  );
}

export function Badge({ value }: { value: string }) {
  return <span className={`badge badge-${value}`}>{label(value)}</span>;
}

// A submission stage as people read it ("interviewing", not "interview"; design v0.6, C2).
export function StageBadge({ stage }: { stage: string | null | undefined }) {
  return <span className={`badge badge-${stage ?? ""}`}>{stageLabel(stage)}</span>;
}

export const formatDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" }) : "—";

// Money shows as currency with two decimals (Nathan, Nov 2, 2026). A cost
// under a cent, such as one AI call, reads "under $0.01" rather than "$0.00".
export const formatMoney = (m: { amount: string | number; currency: string } | undefined) => {
  if (!m) return "—";
  const n = Number(m.amount);
  const format = (v: number) =>
    new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: m.currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(v);
  return n > 0 && n < 0.005 ? `under ${format(0.01)}` : format(n);
};

export const formatUsd = (amount: string | number | null | undefined) =>
  amount == null || amount === "" ? "—" : formatMoney({ amount, currency: "USD" });

// Reads a form's fields as strings, dropping empty ones.
export function formValues(form: HTMLFormElement): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of new FormData(form).entries()) {
    if (typeof v === "string" && v.trim() !== "") out[k] = v.trim();
  }
  return out;
}
